/**
 * Runtime i18n service for translating dynamic keys (e.g. API-returned menu IDs).
 *
 * This complements @lit/localize which handles static, build-time strings.
 * Dynamic keys are looked up from TypeScript modules generated from the XLIFF
 * files via `scripts/xliff-to-json.cjs`.
 */

export type RuntimeTranslations = Record<string, string>;

let currentTranslations: RuntimeTranslations = {};
let currentLocale = 'unknown';

/**
 * Load runtime translations for the given locale.
 * Modules live at `src/generated/runtime-locales/{locale}.ts` and are
 * dynamically imported so only the active locale is loaded.
 */
export async function loadRuntimeTranslations(locale: string): Promise<void> {
  try {
    const mod = await import(`../generated/runtime-locales/${locale}.js`);
    currentTranslations = mod.translations ?? {};
    currentLocale = locale;
  } catch {
    currentTranslations = {};
    currentLocale = locale;
  }
}

/**
 * Translate a dynamic key, falling back to the provided default.
 *
 * Usage:
 *   t('sitemenu.dashboard', 'Dashboard')
 *   t(item.id, item.name)
 */
export function t(key: string | undefined, fallback: string): string {
  if (key == null) {
    return fallback;
  }
  return currentTranslations[key] ?? fallback;
}

/** Current runtime locale (for debugging / tests). */
export function getRuntimeLocale(): string {
  return currentLocale;
}
